/** @jsxRuntime classic */
/** @jsx jsx */
import { useTheme, jsx, SerializedStyles } from '@emotion/react';
import React, { useMemo, Fragment, ReactNode } from 'react';
import { Popup } from '../Popup';
import { StyledSelect, StyledValue, selectedTagsCss } from './style';
import { BnArrowDown, BnArrowUp } from '../../icons';
import { ISelectOptions, SelectOptions } from '../Options_v2';
import { capitalize } from '../../functions';
import { Tooltip } from '../Tooltip';
import { getIcon } from '../../icons';
import { InputContainer } from '../Input/style';
import cx from 'classnames';
import { ErrorMessage } from '../ErrorMessage';
import { Icons } from '../../types/theme';
import { BnIcon } from '../icons_v2';
import { IGroup } from '../Options_v2/types';
import { i18n } from '../../lib/i18n';
import { SelectedTags } from './SelectedTags';
import { LabelTag } from '../../molecules/LabelTag';
import { flexCss, fullWidthCss, minWidthCss } from '../../styles/utils';
import { SearchInput } from './SearchInput';
import { Typography } from '../../atoms';
import { DEFAULT_MAX_WIDTH, DEFAULT_PADDING } from './utils';
import { AdditionalRow, IOptionImage } from '../../types';

export interface IOption_V2 {
	value: string | number;
	label: string;
	icon?: Icons;
	image?: IOptionImage;
	key?: string;
	isDisabled?: boolean;
	additionalInfo?: string;
	additionalRows?: AdditionalRow[];
	rightContent?: React.ReactElement | React.ReactElement[];
	dataTestId?: string;
	dataAddonId?: string | number;
}

export interface ISelect_V2 {
	options: IOption_V2[] | IGroup[];
	placeholder?: string;
	value?: string | number | Array<string | number>;
	selectedLabel?: string | string[] | number;
	onSelect?: (val: string | string[] | number[] | (string | number)[]) => void;
	isMultiple?: boolean;
	sizer?: TSizeEnum;
	buttonColor?: string;
	insideLeftLabel?: string;
	label?: string;
	disabled?: boolean;
	info?: string | ReactNode;
	error?: string;
	helper?: string;
	helperContent?: string;
	clear?: () => void;
	noLabel?: boolean;
	hideError?: boolean;
	/**
	 * Displays `error` immediately, bypassing the "visited" gate. Use for errors that are valid on
	 * load (e.g. a pre-selected value that is no longer available) rather than only after the user
	 * has interacted with the field.
	 */
	displayErrorBeforeVisited?: boolean;
	onChange?: (v: unknown) => void;
	css?: SerializedStyles;
	enableSelectAll?: boolean;
	disableSelectAll?: boolean;
	selectAllLabel?: string;
	unsetOptionsWidth?: boolean;
	isTopLayer?: boolean;
	isDisplayCounter?: boolean;
	isWithTags?: boolean;
	handleType?: (val: string) => void;
	maxSelections?: number;
	isGroups: boolean;
	lastOptionElementRef?: (node: Element) => void;
	isLoading?: boolean;
	maxHeight?: number;
	maxWidth?: string | number;
	customPadding?: string;
	isError?: boolean;
	onClick?: () => void;
	isAllSelected?: boolean;
	handleSelectAll?: () => void;
	isDisplayValue?: boolean;
	marginTop?: number;
	NoResultsComponent?: JSX.Element;
	tags?: ReactNode;
	disableOptionsOnSelectAll?: boolean;
	forceDisplaySelectAllOption?: boolean;
}
import { useSelect } from './useSelect';
import { SizeEnum, TSizeEnum } from '../../lib/constants';

const MAX_HEIGHT = 400;

export const Select_v2 = ({
	options,
	onSelect = val => {},
	value = '',
	placeholder = '',
	sizer = SizeEnum.SMALL,
	buttonColor,
	insideLeftLabel = '',
	label = '',
	noLabel = false,
	disabled = false,
	isMultiple = false,
	info = '',
	helper = '',
	hideError = false,
	displayErrorBeforeVisited = false,
	error = '',
	helperContent = '',
	enableSelectAll = false,
	disableSelectAll,
	selectAllLabel = '',
	handleSelectAll,
	unsetOptionsWidth = false,
	CustomSelectOptionsComponent = undefined,
	clear,
	isTopLayer = false,
	isDisplayCounter = false,
	isWithTags = false,
	handleType,
	maxSelections,
	isGroups,
	lastOptionElementRef,
	isLoading,
	isError,
	maxHeight = MAX_HEIGHT,
	maxWidth = DEFAULT_MAX_WIDTH,
	customPadding = DEFAULT_PADDING,
	selectedLabel,
	isDisplayValue = true,
	onClick,
	isAllSelected,
	marginTop,
	tags,
	disableOptionsOnSelectAll,
	forceDisplaySelectAllOption,
	...rest
}: ISelect_V2 &
	Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onSelect' | 'onChange' | 'value'> & {
		CustomSelectOptionsComponent?: ({
			options,
			onSelect,
			value,
			autoFocus,
			isMultiple,
			enableSelectAll,
			selectAllLabel,
			onSelectAll,
			disableOptionsOnSelectAll,
		}: ISelectOptions) => JSX.Element;
	}) => {
	const {
		handleOnChange,
		isOpen,
		visited,
		ref,
		selectedValues,
		isMaxError,
		handleSetOpen,
		toggleIsOpen,
		handleRemoveOption,
	} = useSelect({ value, maxSelections, onSelect, isWithTags, options, isGroups });

	const colors = useTheme();

	const isDispalyError = ((visited || displayErrorBeforeVisited) && !maxSelections) || (maxSelections && isMaxError);

	const newOptions = isGroups
		? options
		: (options as IOption_V2[])?.map(opt => {
				return {
					...opt,
					value: String(opt.value),
				};
		  });

	const style = rest.style;

	const chosen = isGroups
		? []
		: (options as IOption_V2[]).filter(option => String(option.value) === String(value)).map(option => option.label);

	let multipleChosen: string[] | undefined;
	if (isMultiple) {
		const selectedValues = Array.isArray(value) ? value.map(String) : [];
		multipleChosen = isGroups
			? []
			: (options as IOption_V2[])
					.filter(option => selectedValues.includes(String(option.value)))
					.map(option => option.label);
	}
	const onSelectAll = () => {
		if (handleSelectAll) {
			handleSelectAll();
		} else {
			defaultHandleSelectAll();
		}
	};
	const defaultHandleSelectAll = () => {
		onSelect(
			isGroups
				? []
				: (options as IOption_V2[]).map(item => {
						return item.value;
				  })
		);
	};

	const handleRemoveAll = () => {
		onSelect([]);
	};

	const onChange = (val: string) => {
		handleOnChange(val, isMultiple);
	};

	const basicSelectProps = {
		options: newOptions,
		onSelect: onChange,
		value,
		isGroups,
	};

	const [SelectOptionsComponent, selectProps] = CustomSelectOptionsComponent
		? [CustomSelectOptionsComponent, { ...basicSelectProps }]
		: [
				SelectOptions,
				{
					...basicSelectProps,
					onSelectAll,
					handleRemoveAll,
					isMultiple,
					enableSelectAll,
					disableSelectAll,
					selectAllLabel,
					autoFocus: !handleType,
					lastOptionElementRef,
					isLoading,
					isError,
					isAllSelected,
					disableOptionsOnSelectAll,
					forceDisplaySelectAllOption,
					NoResultsComponent: rest.NoResultsComponent,
				},
		  ];

	const singleChosenLabel = chosen[0];
	const multipleChosenLabel = multipleChosen?.join(', ');

	const selectedValue = useMemo(() => {
		if (!String(value)) {
			return handleType ? '' : placeholder;
		}
		if (isMultiple) {
			const selectedCount = Array.isArray(value) ? value.length : 0;
			return isDisplayCounter ? (
				<LabelTag state={{ label: `${selectedCount} ${i18n.selcted}`, value }} size="XS" />
			) : (
				multipleChosenLabel
			);
		}
		return singleChosenLabel ? capitalize(singleChosenLabel) : undefined;
	}, [handleType, isDisplayCounter, isMultiple, multipleChosenLabel, placeholder, singleChosenLabel, value]);

	const selectedLabelToDisplay = useMemo(() => {
		if (!selectedLabel) {
			return handleType ? '' : placeholder;
		}
		if (isMultiple) {
			return isDisplayCounter ? (
				<LabelTag
					handleRemove={clear}
					state={{ label: `${selectedLabel} ${i18n.selcted}`, value }}
					size={SizeEnum.EXTRA_SMALL}
				/>
			) : (
				multipleChosen?.join(', ')
			);
		}
		return selectedLabel;
	}, [selectedLabel]);

	const memoizedArrow = useMemo(() => (isOpen ? <BnArrowUp /> : <BnArrowDown />), [isOpen]);

	const displayText = useMemo(
		() => (isDisplayValue && selectedValue ? selectedValue : selectedLabelToDisplay),
		[isDisplayValue, selectedValue, selectedLabelToDisplay]
	);

	const handleOnClck = () => {
		toggleIsOpen();
		if (onClick && !disabled) {
			onClick();
		}
	};

	return (
		<Fragment>
			<InputContainer
				className={cx(
					{
						M: sizer === 'M',
						S: sizer === 'S',
					},
					rest.className
				)}
				style={style}
				customPadding={customPadding}
				theme={{ ...colors }}
				disabled={disabled}
				marginTop={marginTop}
				maxWidth={maxWidth}
			>
				{(label || noLabel) && (
					<div className="label" data-aid="index-Select_v2">
						<div>
							{capitalize(label)}
							{rest.required && !noLabel && ' *'}
						</div>
						{info && (
							<Tooltip isTopLayer content={info}>
								{getIcon('info', 'l')}
							</Tooltip>
						)}
					</div>
				)}
				<div data-aid="Select_v2-select-container" className="select-container">
					<Popup
						isTopLayer={isTopLayer}
						fullWidth={!unsetOptionsWidth}
						disableAutoTrigger
						body={disabled ? undefined : <SelectOptionsComponent {...selectProps} />}
						unsetOptionsWidth={unsetOptionsWidth}
						onOpenChange={handleSetOpen}
						padding={'0'}
						borderRadius={0}
						trigger={isOpen}
						maxHeight={maxHeight}
					>
						<StyledSelect
							maxWidth={maxWidth}
							className={cx({
								placeholder: !String(value),
								disabled: disabled,
							})}
							customButtonColor={buttonColor}
							theme={{ ...colors }}
							tabIndex={0}
							ref={ref}
							onClick={() => {
								if (ref.current === document.activeElement) {
									handleOnClck();
								}
							}}
							onFocus={e => {
								handleOnClck();
							}}
						>
							{insideLeftLabel && (
								<Typography color="secondary" type="body2" className="insideLeftLabel">
									{insideLeftLabel}
								</Typography>
							)}
							<div css={[flexCss, fullWidthCss, minWidthCss()]}>
								{displayText && (
									<StyledValue
										className={cx(isDisplayCounter ? 'tagValue' : 'value', {
											error: error && isDispalyError,
											disabled,
										})}
										theme={{ ...colors }}
									>
										{displayText}
									</StyledValue>
								)}
								{handleType && ((!selectedValue && !selectedLabel && clear && !isMultiple) || isMultiple) && (
									<SearchInput
										handleOpenPopup={handleSetOpen}
										onType={handleType}
										placeholder={!String(value) && !selectedLabel ? placeholder : ''}
										disabled={disabled}
									/>
								)}
							</div>
							<div data-aid="Select_v2-button" className="button">
								{memoizedArrow}
							</div>
						</StyledSelect>
					</Popup>
					{clear && !isMultiple && !disabled && (
						<div data-aid="Select_v2-remove" className="remove" css={flexCss} onClick={clear}>
							<BnIcon icon={Icons.remove} />
						</div>
					)}
				</div>
				{!hideError && <div style={{ minHeight: '15px' }}>{isDispalyError && <ErrorMessage error={error} />}</div>}
				{helper && (
					<div data-aid="Select_v2-helper" className="helper">
						{helperContent && <Tooltip content={helperContent}>{getIcon('info', 'r')}</Tooltip>}
						<div>{capitalize(helper)}</div>
					</div>
				)}
			</InputContainer>
			{isWithTags && (
				<div data-aid="Select_v2-selected" css={selectedTagsCss}>
					{tags ? (
						tags
					) : (
						<SelectedTags selectedValues={selectedValues} handleRemove={!disabled ? handleRemoveOption : undefined} />
					)}
				</div>
			)}
		</Fragment>
	);
};
